/**
 * A count of dialogs layered above the review surface (AKL-798).
 *
 * **Why this exists.** The review grid, the full-screen surface and the two dialogs over it all
 * answer the keyboard from listeners bound to `window`, and none of them can see a layer above:
 *
 * - `PointToSpeciesModal` and `ObservationEditor` each register a **capture-phase** `window`
 *   keydown listener that calls `e.stopPropagation()` and then `onCancel()`. React 18
 *   delegates `onKeyDown` from the root container, which is a descendant of `window`, so a
 *   capture-phase listener on `window` runs *first* — and `stopPropagation()` on a
 *   capture-phase listener does not suppress another capture-phase listener on the same
 *   object. A panel-scoped `onKeyDown` on a dialog mounted above them therefore never sees
 *   Escape at all: the review dialog has already cancelled itself and thrown away the species
 *   and comment the reviewer had staged.
 * - `FullScreenImageReview.handleSurfaceKey` owns the single-key review shortcuts and bails
 *   only on `INPUT` / `TEXTAREA` / `SELECT` / `contentEditable`. With a dialog open over the
 *   theater, focus on any button or panel div inside it is none of those, so `s` still runs
 *   `doSave` — committing the review and flipping the photo to Reviewed behind a dialog the
 *   user cannot see past. `a`, `b`, `n`, Shift+S/Enter and the arrow keys are the same.
 * - `ManageReviewPage.handleKeyDown` owns the grid's `Escape` and `s`, and bails on a list of
 *   **that page's own** dialogs (`bulkPending`, `bulkEditorOpen`, `pointToSpeciesOpen`,
 *   `showFilterPopup`). A dialog mounted from anywhere else is not on the list, so with one
 *   open over the grid — theater closed, no review modal up — `s` still ran `toggleBulkMode()`
 *   behind it, entering or leaving bulk mode and throwing away the reviewer's tile selection.
 *
 * A shared module-scope counter is the mechanism, consulted by those four handlers as a
 * single early return. It is a counter rather than a boolean so two layers cannot leave it
 * stuck: each opener pushes on open and pops in its effect's cleanup, and a nested pair
 * unwinds in either order.
 *
 * Deliberately not React state and deliberately not a context: three of the four consumers are
 * `window` listeners inside `useEffect`s with their own dependency arrays, and a context value
 * would not be readable from a listener that must not re-subscribe.
 */

/** How many top-layer dialogs are open. Never negative — see `popTopLayer`. */
let openLayers = 0;

/**
 * Declare that a dialog above the review surface is open.
 *
 * Call it from an effect keyed on the dialog's open state, and pair it with `popTopLayer` in
 * that effect's cleanup so a close, an unmount and a fast reopen all balance.
 */
export function pushTopLayer(): void {
  openLayers += 1;
}

/**
 * Declare that a top-layer dialog has closed.
 *
 * Floored at zero: an unbalanced pop (a double cleanup, or a hot reload that re-ran an effect)
 * must not drive the count negative, because a negative count would make `isTopLayerOpen`
 * report `false` while a layer is still open and hand the keyboard back to the review surface.
 */
export function popTopLayer(): void {
  openLayers = Math.max(0, openLayers - 1);
}

/**
 * Whether a dialog above the review surface currently owns the keyboard.
 * @returns true while at least one top layer is open
 */
export function isTopLayerOpen(): boolean {
  return openLayers > 0;
}
